import { AskRynChartSpec } from '../../services/ask-ryn.service';
import {
  AskRynSqlShare,
  AskRynWorkbenchShare,
} from './ask-ryn-workbench-share';

/** Matches a ```chart fenced block and captures its JSON body. Global+multiline
 * so one reply can carry several charts interleaved with prose. */
const CHART_FENCE = /```chart\s*\n([\s\S]*?)```/g;

/** Matches a ```workbench fenced block (WI 37362): a JSON share of one query. */
const WORKBENCH_FENCE = /```workbench\s*\n([\s\S]*?)```/g;

/** A chart normalized for the template: categorical series (column/bar/line/area)
 * keep `series`; a pie is pre-shaped into `pieData` (Kendo pie wants one series of
 * {category,value} objects). The raw spec stays in `spec` for the axis title etc. */
export interface RenderedChart {
  spec: AskRynChartSpec;
  pieData?: { category: string; value: number }[];
}

/** One "Open in Ryn Workbench" link under a reply; `where` names the connection (and database) it opens on. */
export interface WorkbenchOpenLink {
  href: string;
  where: string;
}

/** Result of splitting an assistant reply into its prose (Markdown), any
 * inline charts and any Workbench open links. */
export interface ParsedAssistantContent {
  markdown: string;
  charts: RenderedChart[];
  workbenchLinks: WorkbenchOpenLink[];
}

/**
 * Split an assistant reply into prose + inline charts + Workbench links. A malformed block is left as-is in the prose
 * so the user still sees something. A valid ```workbench block always becomes a ```sql block in the prose, so the
 * query reads the same with or without a link; the link is added only when the host gives an href for it.
 */
export function parseAssistantContent(
  text: string,
  workbench: AskRynWorkbenchShare | null
): ParsedAssistantContent {
  const charts: RenderedChart[] = [];
  const workbenchLinks: WorkbenchOpenLink[] = [];
  const markdown = text
    .replace(CHART_FENCE, (whole, body: string) => {
      const chart = normalizeChart(body);
      if (!chart) {
        return whole; // not valid JSON / shape — leave the fence in the prose
      }
      charts.push(chart);
      return ''; // pull the chart out of the prose; it renders separately
    })
    .replace(WORKBENCH_FENCE, (whole, body: string) => {
      const share = normalizeShare(body);
      if (!share) {
        return whole;
      }
      const href = workbench?.href(share) ?? null;
      if (href) {
        const where = share.database
          ? `${share.connection} / ${share.database}`
          : share.connection;
        workbenchLinks.push({ href, where });
      }
      return `\`\`\`sql\n${share.sql}\n\`\`\``;
    })
    .trim();
  return { markdown, charts, workbenchLinks };
}

/** Validate + normalize one chart spec body. Returns null on bad JSON or a
 * shape we can't render, so the caller leaves the raw fence in the prose. */
function normalizeChart(body: string): RenderedChart | null {
  let spec: AskRynChartSpec;
  try {
    spec = JSON.parse(body);
  } catch {
    return null;
  }
  const okType = ['column', 'bar', 'line', 'area', 'pie'].includes(spec?.type);
  const okSeries =
    Array.isArray(spec?.series) &&
    spec.series.length > 0 &&
    spec.series.every((s) => Array.isArray(s?.data));
  if (!okType || !okSeries) {
    return null;
  }
  if (spec.type === 'pie') {
    const cats = spec.categories ?? [];
    const data = spec.series[0].data;
    const pieData = data.map((value, i) => ({
      category: cats[i] ?? `#${i + 1}`,
      value,
    }));
    return { spec, pieData };
  }
  return { spec };
}

/** Reply content is untrusted: keep only the share's known fields, each with the right type, and nothing else. */
function normalizeShare(body: string): AskRynSqlShare | null {
  let raw: unknown;
  try {
    raw = JSON.parse(body);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const { sql, connection, database, params } = raw as Record<string, unknown>;
  // A ``` inside the SQL would close the ```sql block it is shown in and let the rest render as Markdown.
  if (typeof sql !== 'string' || !sql.trim() || sql.includes('```')) {
    return null;
  }
  if (typeof connection !== 'string' || !connection.trim()) return null;
  if (database !== undefined && typeof database !== 'string') return null;
  const share: AskRynSqlShare = { sql, connection };
  if (typeof database === 'string' && database) share.database = database;
  if (params !== undefined) {
    const ok =
      Array.isArray(params) &&
      params.every(
        (p) => typeof p?.name === 'string' && typeof p?.value === 'string'
      );
    if (!ok) return null;
    share.params = (params as AskRynSqlShare['params'])!.map((p) => ({
      name: p.name,
      value: p.value,
    }));
  }
  return share;
}
